// RestaurantDashboard (Task 13.3).
//
// Composes the Restaurant's dashboard from independent sections: Overview
// counts, Active Donations, Donation History, NGO Directory (with average
// ratings / "Not yet rated"), Impact stats, and recent achievements, plus a
// Create Donation quick action. Each section loads independently so a failure
// in one never suppresses the others (Req 20.9). Counts and ratings reuse the
// shared domain modules.

import { useState } from "react";
import {
  computeRatingAverage,
  recentAchievements,
  type Achievement,
  type Donation,
  type NGOProfile,
} from "@sharebite/domain";
import { AsyncSection } from "../../components/AsyncSection";
import { CallButton } from "../../components/CallButton";
import { DonationRow } from "../../components/DonationCard";
import { Stat } from "../../components/primitives";
import { badgeName } from "../../lib/badgeDisplay";
import * as api from "../../lib/api";

const ACTIVE_STATUSES = new Set(["Available", "Reserved"]);
const HISTORY_STATUSES = new Set(["Completed", "Expired", "Cancelled"]);

interface DirectoryEntry {
  profile: NGOProfile;
  ratingLabel: string;
}

export function RestaurantDashboard({
  uid,
  onCreateDonation,
}: {
  uid: string;
  onCreateDonation: () => void;
}): JSX.Element {
  async function loadDirectory(): Promise<DirectoryEntry[]> {
    const profiles = await api.listNGOProfiles();
    return Promise.all(
      profiles.map(async (profile) => {
        const ratings = await api.listRatingsForNGO(profile.uid);
        const avg = computeRatingAverage(ratings.map((r) => r.stars));
        return {
          profile,
          // "Not yet rated" when the NGO has zero ratings (Req 16.7, 20.6).
          ratingLabel: avg.hasRatings
            ? avg.average.toFixed(1)
            : "Not yet rated",
        };
      }),
    );
  }

  return (
    <div className="sb-main">
      <div className="sb-row">
        <h1>Restaurant dashboard</h1>
        <span className="sb-spacer" />
        <button type="button" className="sb-btn" onClick={onCreateDonation}>
          Create Donation
        </button>
      </div>

      {/* Overview ---------------------------------------------------------- */}
      <AsyncSection
        title="Overview"
        loader={() => api.restaurantOverview()}
        emptyText=""
      >
        {(counts) => (
          <div className="sb-grid">
            <Stat label="Meals Donated" value={counts.mealsDonated} />
            <Stat label="Meals Completed" value={counts.mealsCompleted} />
            <Stat label="Active Donations" value={counts.activeDonations} />
            <Stat label="Pending Donations" value={counts.pendingDonations} />
          </div>
        )}
      </AsyncSection>

      {/* Active donations -------------------------------------------------- */}
      <AsyncSection
        title="Active donations"
        loader={() => api.listDonationsByRestaurant(uid)}
        emptyText="No active donations."
        isEmpty={(ds: Donation[]) =>
          ds.filter((d) => ACTIVE_STATUSES.has(d.status)).length === 0
        }
      >
        {(ds: Donation[]) => (
          <div>
            {ds
              .filter((d) => ACTIVE_STATUSES.has(d.status))
              .map((d) => (
                <ActiveDonationRow key={d.donationId} donation={d} />
              ))}
          </div>
        )}
      </AsyncSection>

      {/* Donation history -------------------------------------------------- */}
      <AsyncSection
        title="Donation history"
        loader={() => api.listDonationsByRestaurant(uid)}
        emptyText="No past donations."
        isEmpty={(ds: Donation[]) =>
          ds.filter((d) => HISTORY_STATUSES.has(d.status)).length === 0
        }
      >
        {(ds: Donation[]) => (
          <div>
            {ds
              .filter((d) => HISTORY_STATUSES.has(d.status))
              .sort((a, b) => b.createdAt - a.createdAt)
              .map((d) => (
                <HistoryDonationRow key={d.donationId} donation={d} />
              ))}
          </div>
        )}
      </AsyncSection>

      {/* NGO directory ----------------------------------------------------- */}
      <AsyncSection
        title="NGO directory"
        loader={loadDirectory}
        emptyText="No NGOs registered yet."
        isEmpty={(entries: DirectoryEntry[]) => entries.length === 0}
      >
        {(entries: DirectoryEntry[]) => (
          <div>
            {entries.map(({ profile, ratingLabel }) => (
              <div key={profile.uid} className="sb-list-item">
                <div className="sb-row">
                  <strong>{profile.ngoName}</strong>
                  <span className="sb-pill">Rating: {ratingLabel}</span>
                  <span className="sb-spacer" />
                  <CallButton targetUid={profile.uid} label="Call NGO" />
                </div>
              </div>
            ))}
          </div>
        )}
      </AsyncSection>

      {/* Impact ------------------------------------------------------------ */}
      <AsyncSection
        title="Impact"
        loader={() => api.impactStats() as Promise<unknown>}
        emptyText=""
      >
        {(stats) => {
          const impact = (stats as {
            impact: { mealsDonated: number; ngosServed: number };
          }).impact;
          return (
            <div className="sb-grid sb-grid--2">
              <Stat label="Total Meals Donated" value={impact.mealsDonated} />
              <Stat label="NGOs Served" value={impact.ngosServed} />
            </div>
          );
        }}
      </AsyncSection>

      {/* Recent achievements ----------------------------------------------- */}
      <AsyncSection
        title="Recent achievements"
        loader={() => api.listAchievements(uid)}
        emptyText="No achievements earned yet."
        isEmpty={(a: Achievement[]) => a.length === 0}
      >
        {(achievements: Achievement[]) => (
          <div>
            {recentAchievements(achievements).map((a) => (
              <span key={a.achievementId} className="sb-badge">
                {badgeName(a.badgeId)}
              </span>
            ))}
          </div>
        )}
      </AsyncSection>
    </div>
  );
}

// An Active-donation row with the owning Restaurant's cancel action. Only a
// Draft or Available Donation may be cancelled by the Restaurant (Req 12.1);
// the backend remains authoritative (it rejects cancellation of a Reserved
// Donation, Req 12.2). The button is therefore offered only for Available
// rows in this list.
function ActiveDonationRow({ donation }: { donation: Donation }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function cancel(): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await api.cancelDonation({ donationId: donation.donationId });
      setNotice("Donation cancelled. Refresh to see changes.");
    } catch {
      setNotice("Cancellation failed.");
    } finally {
      setBusy(false);
    }
  }

  const canCancel = donation.status === "Available" || donation.status === "Draft";

  return (
    <DonationRow
      donation={donation}
      actions={
        notice ? (
          <span className="sb-muted">{notice}</span>
        ) : canCancel ? (
          <button
            type="button"
            className="sb-btn sb-btn--danger"
            disabled={busy}
            onClick={() => void cancel()}
          >
            {busy ? "Cancelling..." : "Cancel donation"}
          </button>
        ) : null
      }
    />
  );
}

// A history row that lets the owning Restaurant rate the reserving NGO once a
// Donation is Completed (Req 16.1). The star score (1..5) and optional review
// text are submitted through the typed callable; the backend enforces the
// once-per-Donation and ownership rules.
function HistoryDonationRow({ donation }: { donation: Donation }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const ratable = donation.status === "Completed" && donation.reservedBy !== null;

  async function submit(): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await api.submitRating({
        donationId: donation.donationId,
        stars,
        reviewText,
      });
      setNotice("Rating submitted.");
      setOpen(false);
    } catch {
      setNotice("Could not submit rating.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DonationRow
      donation={donation}
      actions={
        !ratable ? null : notice ? (
          <span className="sb-muted">{notice}</span>
        ) : open ? (
          <span className="sb-row" style={{ flexWrap: "wrap" }}>
            <label>
              Stars
              <select
                className="sb-select"
                aria-label="Star rating"
                value={stars}
                onChange={(e) => setStars(Number(e.target.value))}
                style={{ marginLeft: 6 }}
              >
                {[1, 2, 3, 4, 5].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <input
              className="sb-input"
              aria-label="Review text"
              placeholder="Optional review"
              maxLength={500}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              style={{ flex: "1 1 160px" }}
            />
            <button
              type="button"
              className="sb-btn"
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? "Submitting..." : "Submit rating"}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="sb-btn sb-btn--secondary"
            onClick={() => setOpen(true)}
          >
            Rate NGO
          </button>
        )
      }
    />
  );
}
